// src/pages/Support.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config/api";
import { get, post } from "../lib/http";

/* ----------------------- tiny utils ----------------------- */
const cn = (...a) => a.filter(Boolean).join(" ");
const formatDateTime = (v) => {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleString();
};
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const CATEGORIES = [
  "Account",
  "Booking/Order",
  "Payments/Refunds",
  "Technical",
  "Event Listing (Organizer)",
  "Other",
];
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf", "text/plain"];
const MAX_FILE_MB = 8;

/* ----------------------- demo FAQs ------------------------ */
const DEFAULT_FAQS = [
  { q: "How do I change or reset my password?", a: "Go to Profile → Security → Reset Password. You’ll receive an OTP/email to verify the change." },
  { q: "I didn’t receive my booking confirmation email.", a: "Please check Spam/Promotions. If still missing, enter your booking code here or raise a support ticket and we’ll resend it." },
  { q: "How do refunds work?", a: "If your booking is eligible, refunds generally take 5–7 business days after the request is approved. You’ll receive a confirmation email with the refund reference." },
  { q: "I’m an organizer. How do I list my event?", a: "Go to List Your Show → Create Event. Need help? Choose 'Event Listing (Organizer)' in the category and submit details; we’ll reach out." },
  { q: "Payment failed but money deducted.", a: "Sometimes banks show a temporary debit for failed transactions. It auto-reverses within 48–72 hours. If not, raise a ticket with the transaction ID." },
];

/* ----------------------- page ----------------------------- */
export default function Support() {
  const nav = useNavigate();

  // UI/State
  const [q, setQ] = useState("");
  const [faqs] = useState(DEFAULT_FAQS);

  // New ticket form
  const [form, setForm] = useState({
    subject: "",
    category: "Other",
    priority: "Medium",
    bookingCode: "",
    message: "",
    attachments: [], // File[]
    consent: true,
  });
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // My tickets
  const [tickets, setTickets] = useState([]);
  const [tLoading, setTLoading] = useState(false);
  const [tErr, setTErr] = useState("");
  const [tStatus, setTStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ----------------------- load my tickets ----------------------- */
  const loadTickets = async () => {
    setTLoading(true);
    setTErr("");
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.support?.myTickets && typeof API.support.myTickets === "function")
          ? API.support.myTickets({ status: tStatus !== "all" ? tStatus : undefined })
          : `/api/support/tickets?status=${encodeURIComponent(tStatus !== "all" ? tStatus : "")}`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      const items =
        (Array.isArray(res?.data?.tickets) && res.data.tickets) ||
        (Array.isArray(res?.tickets) && res.tickets) ||
        (Array.isArray(res) && res) ||
        [];

      setTickets(items);
    } catch (e) {
      console.error(e);
      setTErr(e?.message || "Failed to load tickets");
      setTickets([]);
    } finally {
      setTLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tStatus]);

  /* ----------------------- derived ----------------------- */
  const filteredFaqs = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return faqs;
    return faqs.filter((f) => f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term));
  }, [faqs, q]);

  const totalPages = Math.max(1, Math.ceil(tickets.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const paged = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return tickets.slice(start, start + pageSize);
  }, [tickets, pageSafe, pageSize]);

  /* ----------------------- handlers ----------------------- */
  const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onFiles = (files) => {
    const list = Array.from(files || []);
    const cleaned = [];
    for (const f of list) {
      if (!ACCEPTED.includes(f.type)) {
        alert(`Unsupported file: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        alert(`File too large: ${f.name} (max ${MAX_FILE_MB} MB)`);
        continue;
      }
      cleaned.push(f);
    }
    setForm((f) => ({ ...f, attachments: [...f.attachments, ...cleaned] }));
  };

  const removeFile = (idx) => {
    setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    if (!form.subject.trim()) return "Please enter a subject.";
    if (!form.message.trim()) return "Please describe your issue.";
    if (!PRIORITIES.includes(form.priority)) return "Invalid priority.";
    if (!CATEGORIES.includes(form.category)) return "Invalid category.";
    if (!form.consent) return "Please confirm consent to contact you.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitMsg("");
    const err = validate();
    if (err) {
      setSubmitMsg(err);
      return;
    }

    try {
      setSubmitting(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const fd = new FormData();
      fd.append("subject", form.subject);
      fd.append("category", form.category);
      fd.append("priority", form.priority);
      if (form.bookingCode) fd.append("bookingCode", form.bookingCode);
      fd.append("message", form.message);
      form.attachments.forEach((f) => fd.append("attachments", f));

      setUploading(true);
      setUploadProgress(15);

      const url =
        (API?.support?.createTicket && typeof API.support.createTicket === "string")
          ? API.support.createTicket
          : (API?.support?.createTicket?.url ||
             API?.support?.createTicket?.path ||
             "/api/support/tickets");

      const resp = await post(url, fd, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (pe) => {
          if (!pe?.total) return;
          const pct = Math.round((pe.loaded / pe.total) * 100);
          setUploadProgress(Math.max(20, pct));
        },
      });

      setUploading(false);
      setUploadProgress(0);

      const ok = resp?.success !== false;
      const ticket = resp?.data?.ticket || resp?.ticket || resp;
      const code = ticket?.code || ticket?.ticketCode || ticket?.id || "";

      setSubmitMsg(ok ? `✅ Ticket created successfully${code ? ` • Code: ${code}` : ""}` : (resp?.message || "Failed to create ticket"));

      if (ok) {
        setForm({
          subject: "",
          category: "Other",
          priority: "Medium",
          bookingCode: "",
          message: "",
          attachments: [],
          consent: true,
        });
        loadTickets();
      }
    } catch (e2) {
      console.error(e2);
      setSubmitMsg(e2?.message || "Failed to submit ticket");
      setUploading(false);
      setUploadProgress(0);
    } finally {
      setSubmitting(false);
    }
  };

  /* ----------------------- UI ----------------------------- */
  return (
    <div className="container mx-auto px-4 md:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav(-1)}
            className="rounded-full border px-3 py-2 text-sm font-semibold hover:bg-amber-50"
            aria-label="Back"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">Support</h1>
            <p className="text-sm text-slate-600">Search FAQs or raise a ticket—our team will help you ASAP.</p>
          </div>
        </div>

        {/* Quick contact — desktop */}
        <div className="hidden sm:flex items-center gap-2">
          <ContactChip label="Email" value="support@example.com" href="mailto:support@example.com" />
          <ContactChip label="Phone" value="+91 98765 43210" href="tel:+919876543210" />
          <ContactChip label="WhatsApp" value="+91 99999 88888" href="https://wa.me/919999988888" />
        </div>
      </div>

      {/* Quick contact — mobile (scrollable chips) */}
      <div className="sm:hidden -mx-4 px-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <ContactChip label="Email" value="support@example.com" href="mailto:support@example.com" />
          <ContactChip label="Phone" value="+91 98765 43210" href="tel:+919876543210" />
          <ContactChip label="WhatsApp" value="+91 99999 88888" href="https://wa.me/919999988888" />
        </div>
      </div>

      {/* Search + categories */}
      <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 md:p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search help topics (refunds, login, booking code, listing…)"
                className="w-full rounded-xl border px-4 py-3 pl-9 bg-white text-sm"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-3.5-3.5" />
              </svg>
            </div>
          </div>
          <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-2 gap-2">
            {["Account", "Booking/Order", "Payments/Refunds", "Event Listing (Organizer)"].map((c) => (
              <button
                key={c}
                onClick={() => {
                  setQ(c);
                  setForm((f) => ({ ...f, category: c }));
                }}
                className="rounded-xl border px-3 py-2 text-sm font-semibold hover:bg-amber-50"
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-600 mb-2">Top FAQs</div>
          <div className="divide-y">
            {filteredFaqs.length === 0 && (
              <div className="text-sm text-slate-500 py-3">No results for “{q}”. Try a different keyword.</div>
            )}
            {filteredFaqs.map((f, i) => (
              <FaqRow key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </div>

      {/* New Ticket + My Tickets */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* New Ticket */}
        <form onSubmit={submit} className="lg:col-span-1 rounded-2xl border bg-white shadow-sm p-5 space-y-4">
          <div className="text-lg font-bold text-slate-900">Create Support Ticket</div>

          <Input
            label="Subject"
            value={form.subject}
            onChange={(v) => onChange("subject", v)}
            required
            placeholder="Short title (e.g. Refund not received)"
          />

          <Select
            label="Category"
            value={form.category}
            onChange={(v) => onChange("category", v)}
            options={CATEGORIES}
          />

          <Select
            label="Priority"
            value={form.priority}
            onChange={(v) => onChange("priority", v)}
            options={PRIORITIES}
          />

          <Input
            label="Booking Code (optional)"
            value={form.bookingCode}
            onChange={(v) => onChange("bookingCode", v)}
            placeholder="e.g. EVT-ABC123-7890"
          />

          <Textarea
            label="Describe your issue"
            value={form.message}
            onChange={(v) => onChange("message", v)}
            rows={6}
            placeholder="Explain what happened. Include booking code, payment id, screenshots…"
          />

          {/* Attachments */}
          <div>
            <Label>Attachments (optional)</Label>
            <div className="mt-1">
              <input
                id="file-input"
                type="file"
                multiple
                accept={ACCEPTED.join(",")}
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
              />
              <label
                htmlFor="file-input"
                className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold hover:bg-slate-50 cursor-pointer"
              >
                Upload files
              </label>
              <div className="mt-2 space-y-1">
                {form.attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded border px-3 py-2 text-xs">
                    <div className="truncate">
                      {f.name} <span className="text-slate-400">({Math.ceil(f.size / 1024)} KB)</span>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} className="text-rose-600 hover:underline">
                      Remove
                    </button>
                  </div>
                ))}
                {uploading && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded bg-slate-200 overflow-hidden">
                      <div className="h-full bg-amber-400" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Allowed: JPG, PNG, WEBP, PDF, TXT • Max {MAX_FILE_MB} MB each
              </div>
            </div>
          </div>

          <Checkbox
            label="I consent to be contacted about this request"
            checked={!!form.consent}
            onChange={(v) => onChange("consent", v)}
          />

          {submitMsg && (
            <div className={cn("text-sm", submitMsg.startsWith("✅") ? "text-emerald-700" : "text-rose-600")}>
              {submitMsg}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="submit"
              disabled={submitting}
              className={cn(
                "rounded-full bg-amber-500 text-white px-5 py-3 font-semibold hover:bg-amber-600",
                submitting && "opacity-60 cursor-wait"
              )}
            >
              {submitting ? "Submitting…" : "Submit Ticket"}
            </button>
            <button
              type="button"
              onClick={() =>
                setForm({
                  subject: "",
                  category: "Other",
                  priority: "Medium",
                  bookingCode: "",
                  message: "",
                  attachments: [],
                  consent: true,
                })
              }
              className="rounded-full border px-5 py-3 font-semibold hover:bg-slate-50"
            >
              Reset
            </button>
          </div>
        </form>

        {/* My Tickets */}
        <div className="lg:col-span-2 rounded-2xl border bg-white shadow-sm p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-lg font-bold text-slate-900">My Tickets</div>
            <div className="flex items-center gap-2">
              <SelectSmall
                value={tStatus}
                onChange={setTStatus}
                options={[
                  { label: "All", value: "all" },
                  { label: "Open", value: "open" },
                  { label: "Pending", value: "pending" },
                  { label: "Resolved", value: "resolved" },
                  { label: "Closed", value: "closed" },
                ]}
              />
              <SelectSmall
                value={String(pageSize)}
                onChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
                options={[10, 20, 50].map((n) => ({ label: `${n}/page`, value: String(n) }))}
              />
            </div>
          </div>

          {tErr && (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
              {tErr}
            </div>
          )}

          {/* Desktop/tablet table */}
          <div className="mt-4 overflow-hidden rounded-xl border hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-amber-50/70 text-slate-600 uppercase text-xs">
                <tr>
                  <Th>Code</Th>
                  <Th>Subject</Th>
                  <Th>Category</Th>
                  <Th>Priority</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tLoading && (
                  <tr>
                    <Td colSpan={6} className="py-10 text-center text-slate-500">
                      Loading tickets…
                    </Td>
                  </tr>
                )}
                {!tLoading && paged.length === 0 && (
                  <tr>
                    <Td colSpan={6} className="py-10 text-center text-slate-500">
                      No tickets found.
                    </Td>
                  </tr>
                )}
                {!tLoading &&
                  paged.map((t) => {
                    const status = statusBadge(t.status);
                    const pr = priorityBadge(t.priority || "Medium");
                    return (
                      <tr key={t.id || t._id}>
                        <Td className="font-mono">{t.code || t.ticketCode || (t.id || t._id)}</Td>
                        <Td className="max-w-[280px] truncate" title={t.subject}>
                          {t.subject}
                        </Td>
                        <Td>{t.category || "—"}</Td>
                        <Td>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                              pr.cls
                            )}
                          >
                            {pr.label}
                          </span>
                        </Td>
                        <Td>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                              status.cls
                            )}
                          >
                            {status.label}
                          </span>
                        </Td>
                        <Td className="whitespace-nowrap">{formatDateTime(t.createdAt)}</Td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden mt-4 space-y-3">
            {tLoading && (
              <div className="rounded-xl border px-4 py-5 text-sm text-slate-500 text-center">
                Loading tickets…
              </div>
            )}
            {!tLoading && paged.length === 0 && (
              <div className="rounded-xl border px-4 py-5 text-sm text-slate-500 text-center">
                No tickets found.
              </div>
            )}
            {!tLoading &&
              paged.map((t) => {
                const status = statusBadge(t.status);
                const pr = priorityBadge(t.priority || "Medium");
                return (
                  <div key={t.id || t._id} className="rounded-2xl border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-slate-500">Code</div>
                        <div className="font-mono text-slate-800 truncate">
                          {t.code || t.ticketCode || (t.id || t._id)}
                        </div>
                      </div>
                      <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold", status.cls)}>
                        {status.label}
                      </span>
                    </div>
                    <div className="mt-3">
                      <div className="text-xs text-slate-500">Subject</div>
                      <div className="font-semibold text-slate-900">{t.subject}</div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-slate-500">Category</div>
                        <div className="text-sm">{t.category || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Priority</div>
                        <div className="text-sm">
                          <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", pr.cls)}>{pr.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-xs text-slate-500">Created</div>
                      <div className="text-sm">{formatDateTime(t.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* footer */}
          <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-600">
            <div>
              Showing <span className="font-semibold">{paged.length}</span> of{" "}
              <span className="font-semibold">{tickets.length}</span>
            </div>
            <Pagination
              page={pageSafe}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              onJump={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- small UI ------------------------- */
function Label({ children }) {
  return <label className="block text-sm font-semibold text-slate-700">{children}</label>;
}
function Input({ label, value, onChange, placeholder, type = "text", required = false }) {
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </Label>
      <input
        type={type}
        className="mt-1 w-full rounded-xl border bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-300"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}
function Textarea({ label, value, onChange, rows = 5, placeholder }) {
  return (
    <div>
      <Label>
        {label}
        <span className="text-rose-600"> *</span>
      </Label>
      <textarea
        className="mt-1 w-full rounded-xl border bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-300"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
      />
    </div>
  );
}
function Select({ label, value, onChange, options = [] }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <select
          className="mt-1 w-full rounded-xl border bg-white px-3 py-2.5 pr-9 outline-none focus:ring-2 focus:ring-amber-300"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </div>
  );
}
function SelectSmall({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        className="rounded-xl border bg-white px-3 py-2 pr-8 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}
function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}
function Th({ children, className = "" }) {
  return <th className={cn("py-2.5 px-3 text-left font-semibold", className)}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={cn("py-2.5 px-3 align-middle", className)}>{children}</td>;
}
function ContactChip({ label, value, href }) {
  return (
    <a
      href={href}
      className="rounded-full border px-3 py-1.5 text-sm font-semibold hover:bg-amber-50 whitespace-nowrap"
      title={value}
    >
      {label}: <span className="text-slate-700">{value}</span>
    </a>
  );
}
function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left font-semibold text-slate-800 hover:underline"
        aria-expanded={open}
      >
        {q}
      </button>
      {open && <div className="mt-1 text-sm text-slate-600">{a}</div>}
    </div>
  );
}
function Pagination({ page, totalPages, onPrev, onNext, onJump }) {
  const pages = buildPageArray(page, totalPages);
  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={onPrev}
        disabled={page <= 1}
        className="rounded-full border px-3 py-2 text-xs font-semibold disabled:opacity-50"
      >
        Prev
      </button>
      {pages.map((p, i) =>
        typeof p === "number" ? (
          <button
            key={i}
            onClick={() => onJump(p)}
            className={cn(
              "rounded-full px-3 py-2 text-xs font-semibold",
              p === page ? "bg-amber-500 text-white" : "border hover:bg-amber-50"
            )}
          >
            {p}
          </button>
        ) : (
          <span key={i} className="px-2 text-slate-400">
            …
          </span>
        )
      )}
      <button
        onClick={onNext}
        disabled={page >= totalPages}
        className="rounded-full border px-3 py-2 text-xs font-semibold disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );
}
function buildPageArray(page, total) {
  const pages = [];
  const show = new Set([1, 2, total - 1, total, page - 1, page, page + 1]);
  const normalized = [...Array(total)]
    .map((_, i) => i + 1)
    .filter((p) => show.has(p) || (p <= 3 && total <= 7));
  let last = 0;
  for (const p of normalized) {
    if (p < 1 || p > total) continue;
    if (p - last > 1) pages.push("…");
    pages.push(p);
    last = p;
  }
  return pages;
}
function statusBadge(s = "") {
  const key = String(s || "open").toLowerCase();
  if (key.includes("pending")) return { label: "Pending", cls: "bg-amber-100 text-amber-800" };
  if (key.includes("resolved")) return { label: "Resolved", cls: "bg-emerald-100 text-emerald-700" };
  if (key.includes("closed")) return { label: "Closed", cls: "bg-slate-200 text-slate-800" };
  return { label: "Open", cls: "bg-blue-100 text-blue-700" };
}
function priorityBadge(p = "Medium") {
  const key = String(p).toLowerCase();
  if (key === "urgent") return { label: "Urgent", cls: "bg-rose-100 text-rose-700" };
  if (key === "high") return { label: "High", cls: "bg-orange-100 text-orange-700" };
  if (key === "low") return { label: "Low", cls: "bg-slate-200 text-slate-800" };
  return { label: "Medium", cls: "bg-amber-100 text-amber-800" };
}

/* --------- OPTIONAL (global CSS): hide horizontal scrollbar on chips) ----------
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
------------------------------------------------------------------------------- */
